import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useAgentContext } from '@Providers';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AgentConversation,
  AgentConversationInteractionsResponse,
  AgentEntry,
  GeneratedFile,
  GuidedJourney,
} from '@Models';
import { bopsApi } from '@Network';
import { setLastAnswer } from '../../Helpers/Agents/lastAnswer';
import {
  createStreamingAnimator,
  MessageConfig,
  processStreamingChunk,
  setupStreamingRequest,
  StreamingStateConfig,
  StreamingStatus,
} from '../AgentStreaming';
import { API_CONFIG, ERROR_PATTERNS } from '../AgentStreaming/constants';
import { StreamingStatus as StreamingStatusEnum, TraceEventType } from '../AgentStreaming/enums';
import useAgents from '../useAgents';
import { useAgentConversations } from './useAgentConversations';
import { INTERACTION_LIMIT, useAgentInteractions } from './useAgentInteractions';
import useAgentRuntime from './useAgentRuntime';

const DEFAULT_CONVERSATION_TITLE = 'Untitled';

interface SendMessageCallbacks {
  onStreamUpdate?: (content: string) => void;
  onOptions?: (items: Array<{ id: string; label: string; hint?: string }>) => void;
  onMultiselect?: (items: Array<{ id: string; label: string; hint?: string }>) => void;
  onChoicegrid?: (
    items: Array<{ id: string; label: string; hint?: string; choices?: Array<{ id: string; label: string }> }>,
  ) => void;
  onFiles?: (files: GeneratedFile[]) => void;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

/**
 * Mirror the backend's `_generate_conversation_title` algorithm so the
 * optimistic title matches the server's: trim, collapse whitespace, cap at
 * 50 chars with an ellipsis. Without this, the sidebar title visibly
 * shortens after the post-stream refetch.
 */
const formatConversationTitle = (raw: string): string => {
  const cleaned = raw.trim().replace(/\s+/g, ' ');
  if (cleaned.length <= 50) return cleaned;
  return `${cleaned.slice(0, 47)}...`;
};

const createNewConversation = (conversationId: string, title?: string): AgentConversation => {
  const now = new Date().toISOString();

  return {
    conversation_id: conversationId,
    title: title ? formatConversationTitle(title) : DEFAULT_CONVERSATION_TITLE,
    timestamps: {
      created_at: now,
      updated_at: now,
      last_interaction_at: now,
    },
    agents_used: [],
    client_name: '',
    user_email: '',
  };
};

const addOptimisticInteraction = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
  userMessage: string,
  selectedAgent: AgentEntry | undefined,
) => {
  queryClient.setQueryData<AgentConversationInteractionsResponse>(
    ['agent-conversation-interactions', conversationId, INTERACTION_LIMIT],
    (oldData) => {
      const newInteraction: any = {
        conversation_id: conversationId,
        interaction_id: `temp_${interactionTimestamp}`,
        timestamp: interactionTimestamp,
        content: {
          input: userMessage,
          output: '',
        },
        agent_metadata: {
          endpoint_name: '',
          retailer_name: selectedAgent?.source_name || '',
          model: '',
        },
        feedback: null,
      };

      if (!oldData) {
        return {
          conversation_id: conversationId,
          interactions: [newInteraction],
        };
      }

      return {
        ...oldData,
        interactions: [...oldData.interactions, newInteraction],
      };
    },
  );
};

const removeOptimisticInteraction = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
) => {
  queryClient.setQueryData<AgentConversationInteractionsResponse>(
    ['agent-conversation-interactions', conversationId, INTERACTION_LIMIT],
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        interactions: oldData.interactions.filter(
          (interaction) => interaction.interaction_id !== `temp_${interactionTimestamp}`,
        ),
      };
    },
  );
};

const updateInteractionInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
  updateFn: (interaction: any) => any,
) => {
  queryClient.setQueryData<AgentConversationInteractionsResponse>(
    ['agent-conversation-interactions', conversationId, INTERACTION_LIMIT],
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        interactions: oldData.interactions.map((interaction) =>
          interaction.timestamp === interactionTimestamp ? updateFn(interaction) : interaction,
        ),
      };
    },
  );
};

const createStreamingCallbacks = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
): SendMessageCallbacks => ({
  onStreamUpdate: (content: string) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      content: { ...interaction.content, output: content },
    }));
  },

  onOptions: (items) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      options: items,
    }));
  },

  onMultiselect: (items) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      multiselect_options: items,
    }));
  },

  onChoicegrid: (items) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      choicegrid_options: items,
    }));
  },

  onFiles: (files) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => {
      const existing = (interaction.generated_files ?? []) as GeneratedFile[];
      const byName = new Map<string, GeneratedFile>(existing.map((f) => [f.file_name, f]));
      files.forEach((f) => byName.set(f.file_name, f));
      return { ...interaction, generated_files: Array.from(byName.values()) };
    });
  },

  onComplete: () => {
    queryClient.invalidateQueries({ queryKey: ['agent-conversations'] }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error invalidating conversations after message complete:', error);
    });
  },

  onError: (error: string) => {
    // eslint-disable-next-line no-console
    console.error('Error sending message:', error);

    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      content: { ...interaction.content, output: `Error: ${error}` },
    }));
  },
});

interface RuntimeBindings {
  setThinkingForConversation: (value: boolean) => void;
  appendStepForConversation: (eventType: string, message: string) => void;
}

const executeStreamingRequest = async (
  prompt: string,
  conversationId: string,
  selectedAgent: AgentEntry | undefined,
  abortSignal: AbortSignal,
  streamingState: StreamingStateConfig,
  callbacks: SendMessageCallbacks,
  runtime: RuntimeBindings,
  displayText?: string,
  pendingJourneyContext?: string | null,
  setPendingJourneyContext?: (value: string | null) => void,
  selectedJourney?: string,
  journeyCacheFile?: string,
  interactionTimestamp?: string,
): Promise<void> => {
  const animator = createStreamingAnimator((visibleText) => {
    callbacks.onStreamUpdate?.(visibleText);
  }, abortSignal);

  const messageConfig: MessageConfig = {
    receiveAssistantMessage: (content: string) => {
      callbacks.onStreamUpdate?.(content);
    },
    updateStreamingMessage: (content: string) => {
      animator.update(content);
    },
  };

  setupStreamingRequest(streamingState);

  let hasReceivedContent = false;
  let finalContent = '';
  let hasReceivedDoneEvent = false;

  const streamGenerator = bopsApi.streamAgentJSON(
    {
      prompt,
      conversation_id: conversationId,
      max_prompt_length: API_CONFIG.MAX_PROMPT_LENGTH,
      trace: API_CONFIG.TRACE_ENABLED,
      source_name: selectedAgent?.source_name || null,
      display_text: displayText,
      context_file: pendingJourneyContext || undefined,
      selected_journey: selectedJourney,
      journey_cache_file: journeyCacheFile,
      interaction_timestamp: interactionTimestamp,
    },
    abortSignal,
  );

  if (pendingJourneyContext && setPendingJourneyContext) {
    setPendingJourneyContext(null);
  }

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value: event } = await streamGenerator.next();

      if (done) {
        break;
      }

      if (event.type === 'chunk') {
        const result = processStreamingChunk(
          event.text,
          hasReceivedContent,
          finalContent,
          conversationId,
          streamingState,
          messageConfig,
        );
        hasReceivedContent = result.hasReceivedContent;
        finalContent = result.finalContent;
      }

      if (event.type === 'trace') {
        if (streamingState.isThinkingRef.current) {
          const eventType = event.event_type;
          const traceMessage = event.message;

          // eslint-disable-next-line no-continue
          if (eventType === TraceEventType.CODE_INTERPRETER_ERROR) continue;
          // eslint-disable-next-line no-continue
          if (eventType === TraceEventType.DEFAULT) continue;
          // athena_query_retry marks a transient timeout the tool absorbed
          // with an internal retry. It's persisted to DDB for the admin trace
          // (amber "stalled — retried" card), but the user should keep seeing
          // the previous step, not a new one — so skip it here.
          // eslint-disable-next-line no-continue
          if (eventType === TraceEventType.ATHENA_QUERY_RETRY) continue;
          // Rehydration is debug telemetry (whether the runtime rebuilt
          // context from the payload, the in-process cache or started
          // empty). It's persisted to DDB / CloudWatch for diagnostics
          // but should never reach the user's thinking-step list.
          // eslint-disable-next-line no-continue
          if (eventType === TraceEventType.REHYDRATION) continue;
          // summarization_start shows the "Auto-compactando…" indicator;
          // the matching end event is dropped so the next real thinking
          // step replaces the indicator naturally without a flicker.
          // eslint-disable-next-line no-continue
          if (eventType === TraceEventType.SUMMARIZATION_END) continue;

          // Reducer dedups by eventType internally.
          runtime.appendStepForConversation(eventType, traceMessage);
        }
      }

      if (event.type === 'done') {
        hasReceivedDoneEvent = true;

        if (event.metrics?.cached && journeyCacheFile && setPendingJourneyContext) {
          setPendingJourneyContext(journeyCacheFile);
        }

        // eslint-disable-next-line no-await-in-loop
        await streamGenerator.return(undefined);
        break;
      }

      if (event.type === 'files') {
        const generatedFiles: GeneratedFile[] = event.files.map((f) => ({
          file_name: f.name,
          file_size: f.size ?? 0,
          file_type: f.type ?? 'application/octet-stream',
        }));
        if (generatedFiles.length > 0) {
          callbacks.onFiles?.(generatedFiles);
        }
      }

      if (event.type === 'options') {
        const items = event.items.map((it) => ({
          id: it.id,
          label: it.label,
          ...(it.hint ? { hint: it.hint } : {}),
        }));
        callbacks.onOptions?.(items);
      }

      if (event.type === 'multiselect') {
        const items = event.items.map((it) => ({
          id: it.id,
          label: it.label,
          ...(it.hint ? { hint: it.hint } : {}),
        }));
        callbacks.onMultiselect?.(items);
      }

      if (event.type === 'choicegrid') {
        // `choices` is what makes a grid row actionable, so it is carried
        // through rather than flattened away like the other kinds.
        const items = event.items.map((it) => ({
          id: it.id,
          label: it.label,
          ...(it.hint ? { hint: it.hint } : {}),
          ...(it.choices ? { choices: it.choices.map((c) => ({ id: c.id, label: c.label })) } : {}),
        }));
        callbacks.onChoicegrid?.(items);
      }
    }
  } catch (streamError) {
    if (hasReceivedDoneEvent && hasReceivedContent) {
      // eslint-disable-next-line no-console
      console.warn('Stream completed successfully, ignoring connection closure error:', streamError);
    } else {
      animator.cancel();
      throw streamError;
    }
  }

  await animator.waitForCompletion();

  streamingState.setStreamingStatus(StreamingStatusEnum.COMPLETED);

  if (!hasReceivedContent) {
    messageConfig.receiveAssistantMessage(streamingState.noResponseMessage, undefined, conversationId);
  }
};

const isRequestCanceled = (err: unknown): boolean => {
  if (typeof err === 'object' && err !== null && ('name' in err || 'message' in err)) {
    const anyErr = err as { name?: string; message?: string };
    return (
      ERROR_PATTERNS.CANCELED_ERROR_NAMES.includes(anyErr.name as any) ||
      ERROR_PATTERNS.CANCELED_ERROR_MESSAGES.includes(anyErr.message as any)
    );
  }
  return false;
};

const getErrorMessage = (err: unknown, t: ReturnType<typeof useTranslation>['t'], lng: string): string => {
  if (!(err instanceof Error)) {
    return t('Agent.ErrorUnexpected', { lng });
  }

  const anyErr = err as Error & { code?: string };

  if (ERROR_PATTERNS.TIMEOUT_MESSAGES.some((msg) => err.message.includes(msg)) || anyErr.code === 'ECONNABORTED') {
    return t('Agent.ErrorTimeout', { lng });
  }

  if (err.message === ERROR_PATTERNS.NETWORK_ERROR_MESSAGE) {
    return t('Agent.ErrorNetwork', { lng });
  }

  // The backend refuses to run a guided journey whose instructions it cannot
  // load, rather than letting the agent improvise. Say so plainly — the generic
  // message would leave the user unsure whether anything ran.
  if (err.message.includes(ERROR_PATTERNS.JOURNEY_PROMPT_UNAVAILABLE)) {
    return t('Agent.ErrorJourneyUnavailable', { lng });
  }

  return t('Agent.ErrorUnexpected', { lng });
};

const useChatManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeChatId, setActiveChatId, selectedAgent, setSelectedAgent } = useAgentContext();
  const { addConversationToCache, removeConversationFromCache, updateConversationInCache, conversations } =
    useAgentConversations(selectedAgent?.source_name);
  const { data: agents } = useAgents();
  const { isAccessDenied } = useAgentInteractions(activeChatId);
  const {
    getRuntime,
    isInFlight,
    startSend,
    setThinking,
    setThinkingMessage: setRuntimeThinkingMessage,
    appendStep,
    finishSend,
    cancelSend,
    markRead,
  } = useAgentRuntime();

  // Streaming-only state (does not need to survive unmount)
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingStatus, setStreamingStatus] = useState<StreamingStatus>(StreamingStatusEnum.IDLE);

  // Always-fresh ref for the active chat id — needed so finishSend can decide
  // markUnread synchronously regardless of which closure captured activeChatId.
  const activeChatIdRef = useRef(activeChatId);
  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  const [pendingJourneyContext, setPendingJourneyContext] = useState<string | null>(null);

  const sendUserMessage = useCallback(
    async (
      message: string,
      displayText?: string,
      selectedJourney?: string,
      journeyCacheFile?: string,
      existingConversationId?: string,
    ) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) return;

      // Asking anything else is engagement: the previous answer was not abandoned.
      setLastAnswer(null);

      const wasNewConversation = !existingConversationId && (!activeChatId || isAccessDenied);
      let conversationId = existingConversationId || activeChatId;
      if (!conversationId || isAccessDenied) {
        conversationId = uuidv4();
        addConversationToCache(createNewConversation(conversationId, trimmedMessage));
        setActiveChatId(conversationId);
      }

      // Sync guard against double-sends in the same frame: if a controller
      // is already registered for this id, ignore the second invocation.
      // Reads from the ref instead of state so the guard holds before React
      // has flushed the `isLoading` update.
      if (isInFlight(conversationId)) return;

      const signal = startSend(conversationId, trimmedMessage);
      const interactionTimestamp = new Date().toISOString();

      const localIsThinkingRef = { current: false };
      const localSeenEventTypesRef = { current: new Set<string>() };

      const agentLng = (selectedAgent?.language || 'en').toLowerCase();

      try {
        const userInputText = displayText || trimmedMessage;
        addOptimisticInteraction(queryClient, conversationId, interactionTimestamp, userInputText, selectedAgent);

        const prompt = trimmedMessage;
        const callbacks = createStreamingCallbacks(queryClient, conversationId, interactionTimestamp);

        const sendId = conversationId;
        const streamingState: StreamingStateConfig = {
          setStreamingStatus,
          setStreamingContent,
          setIsThinking: (v) => setThinking(sendId, v),
          isThinkingRef: localIsThinkingRef,
          setThinkingMessage: (msg) => setRuntimeThinkingMessage(sendId, msg),
          seenEventTypesRef: localSeenEventTypesRef,
          initialThinkingMessage: t('Agent.ThinkingInitial', { lng: agentLng }),
          noResponseMessage: t('Agent.ErrorNoResponse', { lng: agentLng }),
        };

        await executeStreamingRequest(
          prompt,
          conversationId,
          selectedAgent,
          signal,
          streamingState,
          callbacks,
          {
            setThinkingForConversation: (v) => setThinking(sendId, v),
            appendStepForConversation: (eventType, msg) => appendStep(sendId, eventType, msg),
          },
          displayText,
          pendingJourneyContext,
          setPendingJourneyContext,
          selectedJourney,
          journeyCacheFile,
          interactionTimestamp,
        );

        callbacks.onComplete?.();
        // A cancelled or failed answer was never there to be abandoned.
        setLastAnswer({
          conversationId,
          interactionTimestamp,
          completedAtMs: Date.now(),
          actedSince: false,
        });
        finishSend(conversationId, activeChatIdRef.current === conversationId);
      } catch (err: unknown) {
        if (isRequestCanceled(err)) {
          // Cancel cleanup: optimistic interaction goes away. If the conversation
          // was created by THIS send, drop it from the sidebar so it doesn't ghost.
          removeOptimisticInteraction(queryClient, conversationId, interactionTimestamp);
          if (wasNewConversation) {
            removeConversationFromCache(conversationId);
            if (activeChatIdRef.current === conversationId) setActiveChatId(null);
          }
          return;
        }

        const errorMessage = getErrorMessage(err, t, agentLng);
        // eslint-disable-next-line no-console
        console.error('Error in sendUserMessage:', err);

        updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
          ...interaction,
          content: { ...interaction.content, output: `Error: ${errorMessage}` },
        }));

        setStreamingStatus(StreamingStatusEnum.ERROR);
        finishSend(conversationId, activeChatIdRef.current === conversationId);
      } finally {
        setStreamingContent('');
        setStreamingStatus(StreamingStatusEnum.IDLE);
      }
    },
    [
      activeChatId,
      setActiveChatId,
      selectedAgent,
      addConversationToCache,
      removeConversationFromCache,
      queryClient,
      pendingJourneyContext,
      setPendingJourneyContext,
      isAccessDenied,
      t,
      isInFlight,
      startSend,
      setThinking,
      setRuntimeThinkingMessage,
      appendStep,
      finishSend,
    ],
  );

  const executeJourney = useCallback(
    async (journey: GuidedJourney) => {
      try {
        const displayWithTitle = journey.title ? `${journey.display} - ${journey.title}` : journey.display;

        let conversationId = activeChatId;
        if (!conversationId) {
          conversationId = uuidv4();
          addConversationToCache(createNewConversation(conversationId, displayWithTitle));
          setActiveChatId(conversationId);
        }

        const cacheFilename = journey.cache?.s3_path?.filename;
        // Deliberately NOT journey.prompt. The backend loads the prompt from S3
        // itself on every journey turn (keyed off the journey id it receives
        // here), so sending it duplicated ~7.7KB into the request body — enough
        // to cross the 8KB WAF limit and have the whole request rejected at the
        // load balancer, with no server log and no usable error.
        await sendUserMessage(displayWithTitle, displayWithTitle, journey.id, cacheFilename, conversationId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error executing journey:', error);
      }
    },
    [activeChatId, setActiveChatId, addConversationToCache, sendUserMessage],
  );

  const switchChat = useCallback(
    (conversationId: string | null) => {
      setActiveChatId(conversationId);
      if (conversationId) markRead(conversationId);

      if (conversationId && agents) {
        const conversation = conversations.find((c) => c.conversation_id === conversationId);

        if (conversation && conversation.agents_used && conversation.agents_used.length > 0) {
          const firstAgentUsed = conversation.agents_used[0];
          const retailerName = firstAgentUsed.retailer_name;

          const agent = agents.find((a) => a.source_name === retailerName);

          if (agent) {
            setSelectedAgent(agent);
          }
        }
      }
    },
    [setActiveChatId, conversations, agents, setSelectedAgent, markRead],
  );

  /** Stops in-flight generation; returns the prompt so the caller can repopulate the input. */
  const stopGeneration = useCallback(
    (conversationId?: string): string | null => {
      const targetId = conversationId ?? activeChatIdRef.current;
      if (!targetId) return null;
      const restoredPrompt = cancelSend(targetId);
      setStreamingStatus(StreamingStatusEnum.IDLE);
      setStreamingContent('');
      return restoredPrompt;
    },
    [cancelSend],
  );

  const deleteConversation = useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await bopsApi.deleteConversation(conversationId);
    },

    onMutate: async (conversationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['agent-conversations'] });
      const previousConversations = queryClient.getQueryData(['agent-conversations', 20]);
      // Abort any in-flight stream for this conversation and clear its runtime slot.
      cancelSend(conversationId);
      removeConversationFromCache(conversationId);
      if (activeChatId === conversationId) {
        setActiveChatId(null);
      }
      return { previousConversations, deletedConversationId: conversationId };
    },

    onError: (error, conversationId, context) => {
      // eslint-disable-next-line no-console
      console.error('Error deleting conversation:', error);
      if (context?.previousConversations) {
        queryClient.setQueryData(['agent-conversations', 20], context.previousConversations);
      }
      if (context?.deletedConversationId === conversationId && activeChatId === null) {
        setActiveChatId(conversationId);
      }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: ['agent-conversations'] }),
  });

  const updateConversationTitle = useMutation({
    mutationFn: async ({ conversationId, title }: { conversationId: string; title: string }): Promise<void> => {
      await bopsApi.updateConversationTitle(conversationId, title);
    },

    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey: ['agent-conversations'] });
      const previousConversations = queryClient.getQueryData(['agent-conversations', 20]);
      updateConversationInCache(conversationId, { title });
      return { previousConversations, conversationId, oldTitle: title };
    },

    onError: (error, _variables, context) => {
      // eslint-disable-next-line no-console
      console.error('Error updating conversation title:', error);
      if (context?.previousConversations) {
        queryClient.setQueryData(['agent-conversations', 20], context.previousConversations);
      }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: ['agent-conversations'] }),
  });

  const activeRuntime = getRuntime(activeChatId);

  return {
    activeChatId,
    sendUserMessage,
    executeJourney,
    switchChat,
    stopGeneration,
    deleteConversation,
    updateConversationTitle,
    isGenerating: activeRuntime.isLoading,
    streamingContent,
    streamingStatus,
    isThinking: activeRuntime.isThinking,
    thinkingMessage: activeRuntime.thinkingMessage,
    thinkingSteps: activeRuntime.thinkingSteps,
    startedAtMs: activeRuntime.startedAtMs,
  };
};

export default useChatManagement;
